import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import Swal from "sweetalert2";
import AuthService from "../../pages/Authentication/auth.service.js";
import DiscountCouponService from "../../pages/DiscountCoupon/Discountcoupons.service.js";

const ACCOUNT_CONFIG = {
  doctor: {
    sendCredentials: DiscountCouponService.sendDoctorCredentials,
    resetCredentials: DiscountCouponService.resetDoctorCredentials,
    sendConfirmText:
      "Current login credentials will be sent to the doctor's email. The password will not be changed.",
    resetConfirmText:
      "A new password will be generated and sent to the doctor's email. The doctor will be logged out from all devices.",
    successText: "Login credentials have been sent to the doctor email",
  },
  patient: {
    sendCredentials: DiscountCouponService.sendPatientCredentials,
    resetCredentials: DiscountCouponService.resetPatientCredentials,
    sendConfirmText:
      "Current login credentials will be sent to the patient's email. The password will not be changed.",
    resetConfirmText:
      "A new password will be generated and sent to the patient's email. The patient will be logged out from all devices.",
    successText: "Login credentials have been sent to the patient email",
  },
};

const AccountCredentialActions = ({ accountType, accountId, disabled = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sendingCredentials, setSendingCredentials] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  const config = ACCOUNT_CONFIG[accountType];
  const sessionID = localStorage.getItem("session-id");

  if (!config || !accountId) {
    return null;
  }

  const handleUnauthorized = (message) => {
    if (message === "Unauthorized") {
      AuthService.logout();
      navigate("/login");
      window.location.reload();
    }
  };

  const handleCredentialResponse = (response) => {
    const message = response?.data?.message || "";

    if (message.includes("not yet verified")) {
      Swal.fire({
        icon: "warning",
        title: t("Email Not Verified"),
        text: message,
        confirmButtonColor: "#00BFA6",
      });
      return;
    }

    Swal.fire({
      icon: "success",
      title: t("Credentials Sent"),
      text: message || t(config.successText),
      confirmButtonColor: "#00BFA6",
    });
  };

  const handleSendCredentials = async () => {
    const result = await Swal.fire({
      title: t("Send Credentials?"),
      text: t(config.sendConfirmText),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00BFA6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("Yes, send credentials"),
      cancelButtonText: t("Cancel"),
    });

    if (!result.isConfirmed) return;

    try {
      setSendingCredentials(true);
      const response = await config.sendCredentials(sessionID, accountId);

      if (response.data.success) {
        handleCredentialResponse(response);
      } else {
        handleUnauthorized(response.data.message);
        Swal.fire({
          icon: "error",
          title: t("Failed to Send Credentials"),
          text: response.data.message || t("An error occurred while sending login credentials"),
        });
      }
    } catch (error) {
      console.error("Error sending credentials:", error);
      handleUnauthorized(error.response?.data?.message);
      Swal.fire({
        icon: "error",
        title: t("Failed to Send Credentials"),
        text:
          error.response?.data?.message ||
          t("An error occurred while sending login credentials"),
      });
    } finally {
      setSendingCredentials(false);
    }
  };

  const handleResetPassword = async () => {
    const result = await Swal.fire({
      title: t("Reset Password?"),
      text: t(config.resetConfirmText),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00BFA6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("Yes, reset password"),
      cancelButtonText: t("Cancel"),
    });

    if (!result.isConfirmed) return;

    try {
      setResettingPassword(true);
      const response = await config.resetCredentials(sessionID, accountId);

      if (response.data.success) {
        handleCredentialResponse(response);
      } else {
        handleUnauthorized(response.data.message);
        Swal.fire({
          icon: "error",
          title: t("Failed to Reset Password"),
          text: response.data.message || t("An error occurred while resetting the password"),
        });
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      handleUnauthorized(error.response?.data?.message);
      Swal.fire({
        icon: "error",
        title: t("Failed to Reset Password"),
        text:
          error.response?.data?.message ||
          t("An error occurred while resetting the password"),
      });
    } finally {
      setResettingPassword(false);
    }
  };

  const isBusy = disabled || sendingCredentials || resettingPassword;

  return (
    <>
      <Tooltip placement="top" title={t("Send Login Credentials")} arrow>
        <button
          type="button"
          onClick={handleSendCredentials}
          disabled={isBusy}
          className="btn btn-success btn-sm btn-hover-shine me-2"
        >
          {sendingCredentials ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {t("Sending...")}
            </>
          ) : (
            <>
              <i className="pe-7s-mail"></i> {t("Send Credentials")}
            </>
          )}
        </button>
      </Tooltip>
      <Tooltip placement="top" title={t("Reset Password")} arrow>
        <button
          type="button"
          onClick={handleResetPassword}
          disabled={isBusy}
          className="btn btn-warning btn-sm btn-hover-shine me-2"
        >
          {resettingPassword ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {t("Resetting...")}
            </>
          ) : (
            <>
              <i className="pe-7s-key"></i> {t("Reset Password")}
            </>
          )}
        </button>
      </Tooltip>
    </>
  );
};

export default AccountCredentialActions;
